import { useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "../lib/supabase";
import { useLang, useTheme } from "../lib/prefs";
import { avatarUrl, displayName } from "../lib/profile";
import { PrefsToggles } from "./PrefsToggles";
import { SitesProvider, useSites } from "../lib/sites";
import type { Site } from "../lib/database.types";
import SiteSelector from "./SiteSelector";
import CreateSiteModal from "./CreateSiteModal";
import Personas from "./Personas";
import Dashboard from "./Dashboard";
import Profile from "./Profile";
import Billing from "./Billing";
import UpgradeModal from "./UpgradeModal";

type View = "dash" | "personas" | "billing" | "profile";

const T = {
  es: {
    nvDash: "Panel", nvPer: "Perfiles", nvBill: "Facturación", nvProf: "Perfil",
    planLab: "Plan del sitio", upg: "Mejorar plan",
    signout: "Cerrar sesión", welcome: "Bienvenido",
    soon: "El panel de visibilidad de este sitio aparecerá aquí. Estamos configurando tus perfiles.",
    apiOk: "Sesión verificada por el API", apiFail: "El API no validó la sesión",
    loadErr: "No se pudieron cargar tus sitios.",
    trialDays: (n: number) => `Pro · prueba (${n} días)`,
    cadWeekly: "reportes semanales", cadMonthly: "reporte mensual",
    noSite: "Sin sitio seleccionado",
    planFree: "Gratis", planPro: "Pro", planEnt: "Enterprise",
  },
  en: {
    nvDash: "Dashboard", nvPer: "Personas", nvBill: "Billing", nvProf: "Profile",
    planLab: "Site plan", upg: "Upgrade",
    signout: "Sign out", welcome: "Welcome",
    soon: "This site's visibility dashboard will live here. We're setting up your personas.",
    apiOk: "Session verified by the API", apiFail: "API did not validate the session",
    loadErr: "Could not load your sites.",
    trialDays: (n: number) => `Pro · trial (${n} days)`,
    cadWeekly: "weekly reports", cadMonthly: "monthly report",
    noSite: "No site selected",
    planFree: "Free", planPro: "Pro", planEnt: "Enterprise",
  },
};

const NAV_ICONS = {
  dash: "M3 12h7V3H3zM14 21h7v-9h-7zM3 21h7v-5H3zM14 9h7V3h-7z",
  personas: "M3 20a6 6 0 0112 0M17 6a3 3 0 010 6M21 20a6 6 0 00-4-5.7",
  billing: "M2 5h20v14H2zM2 10h20",
  profile: "M4 21a8 8 0 0116 0",
};

// AppShell wires the whole authed app to the caller's sites: the SitesProvider
// loads them and tracks the selected one; every site-scoped view (M3+) reads it
// from useSites(). Workspace holds the chrome and content.
export default function AppShell({ session }: { session: Session }) {
  return (
    <SitesProvider token={session.access_token}>
      <Workspace session={session} />
    </SitesProvider>
  );
}

function daysLeft(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

function planLabel(site: Site, t: (typeof T)["es"]): string {
  if (site.plan === "pro") return t.trialDays(daysLeft(site.trial_ends_at));
  if (site.plan === "enterprise") return t.planEnt;
  return t.planFree;
}

function Workspace({ session }: { session: Session }) {
  const [lang, toggleLang] = useLang();
  const [, toggleTheme] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState<"idle" | "ok" | "fail">("idle");
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState<View>("dash");
  const [upgradeSite, setUpgradeSite] = useState<Site | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const t = T[lang];

  const { sites, loading, error, selectedSite } = useSites();

  const user = session.user;
  const name = displayName(user) ?? user.email ?? "—";
  const avatar = avatarUrl(user);
  const initial = (name || "?").charAt(0).toUpperCase();

  // Smoke the authed API path from the browser: /api/me must accept the bearer.
  useEffect(() => {
    let active = true;
    fetch("/api/me", {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { ok: boolean }) => active && setApiStatus(d.ok ? "ok" : "fail"))
      .catch(() => active && setApiStatus("fail"));
    return () => {
      active = false;
    };
  }, [session.access_token]);

  // Close the account menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  async function signOut() {
    await getSupabase().auth.signOut();
    window.location.replace("/login");
  }

  // Zero sites → force the onboarding create flow (no dismissal).
  const mustCreate = !loading && !error && sites.length === 0;

  return (
    <div className="app">
      <aside className="side">
        <div className="logo">
          <div className="wm">citada</div>
        </div>
        <nav className="nav">
          <a className={view === "dash" ? "on" : ""} onClick={() => setView("dash")}>
            <NavIcon d={NAV_ICONS.dash} />
            <span>{t.nvDash}</span>
          </a>
          <a className={view === "personas" ? "on" : ""} onClick={() => setView("personas")}>
            <NavIcon d={NAV_ICONS.personas} />
            <span>{t.nvPer}</span>
          </a>
          <a className={view === "billing" ? "on" : ""} onClick={() => setView("billing")}>
            <NavIcon d={NAV_ICONS.billing} rect />
            <span>{t.nvBill}</span>
          </a>
          <a className={view === "profile" ? "on" : ""} onClick={() => setView("profile")}>
            <NavIcon d={NAV_ICONS.profile} circle />
            <span>{t.nvProf}</span>
          </a>
        </nav>
        <div className="side-foot">
          <div className="planbox">
            <div className="t">{t.planLab}</div>
            <div className="p">
              {selectedSite ? planLabel(selectedSite, t) : t.noSite}
            </div>
            <button
              className="btn btn-primary btn-block"
              disabled={!selectedSite}
              onClick={() => selectedSite && setUpgradeSite(selectedSite)}
            >
              {t.upg}
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="top">
          <SiteSelector lang={lang} onNewSite={() => setShowCreate(true)} />
          <span className="sp" />
          <PrefsToggles
            lang={lang}
            toggleLang={toggleLang}
            toggleTheme={toggleTheme}
          />
          <div className="topwrap" ref={menuRef}>
            <button
              className="avatar-g"
              aria-label={t.nvProf}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {avatar ? (
                <img src={avatar} alt="" referrerPolicy="no-referrer" />
              ) : (
                <span className="ph">{initial}</span>
              )}
            </button>
            {menuOpen && (
              <div className="drop acct">
                <div className="who">
                  <b>{name}</b>
                  <div className="e">{user.email}</div>
                </div>
                <a onClick={() => { setView("profile"); setMenuOpen(false); }}>{t.nvProf}</a>
                <a onClick={() => { setView("billing"); setMenuOpen(false); }}>{t.nvBill}</a>
                <a onClick={signOut}>{t.signout}</a>
              </div>
            )}
          </div>
        </header>

        <div className="content">
          {view === "dash" ? (
            <Dashboard lang={lang} token={session.access_token} />
          ) : view === "personas" ? (
            <Personas lang={lang} token={session.access_token} />
          ) : view === "profile" ? (
            <Profile lang={lang} session={session} />
          ) : view === "billing" ? (
            <Billing
              lang={lang}
              token={session.access_token}
              onUpgrade={(s) => setUpgradeSite(s)}
            />
          ) : (
            <>
              <div className="h-row">
                <h1>
                  {t.welcome}
                  {name ? `, ${name.split(" ")[0]}` : ""}
                </h1>
                {selectedSite && <span className="sub">{selectedSite.domain}</span>}
              </div>
              <p className="sub-line">
                {apiStatus === "ok" && (
                  <>
                    <b>✓</b> {t.apiOk}
                  </>
                )}
                {apiStatus === "fail" && (
                  <>
                    <b>×</b> {t.apiFail}
                  </>
                )}
              </p>
              {error && <p className="auth-err">{t.loadErr}</p>}
              <div className="card">
                <div className="card-h">
                  <h3>{t.nvProf}</h3>
                </div>
                <p style={{ color: "var(--muted)", padding: "0 16px 18px", margin: 0 }}>
                  {t.soon}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {(showCreate || mustCreate) && (
        <CreateSiteModal
          lang={lang}
          mandatory={mustCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {upgradeSite && (
        <UpgradeModal
          lang={lang}
          site={upgradeSite}
          email={user.email ?? ""}
          onClose={() => setUpgradeSite(null)}
        />
      )}
    </div>
  );
}

function NavIcon({
  d,
  circle,
  rect,
}: {
  d: string;
  circle?: boolean;
  rect?: boolean;
}) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      {circle && <circle cx="12" cy="8" r="4" />}
      {rect && <rect x="2" y="5" width="20" height="14" rx="2" />}
      <path d={d} />
    </svg>
  );
}
